/**
 * Plugin Execution Wrapper — Wave 22.13.9
 *
 * Utility functions that wrap plugin action handlers, event handlers,
 * contentSync subscribers, and modal callbacks with:
 *   1. try/catch error isolation
 *   2. Slow-execution detection (>250ms)
 *   3. Stability store recording
 *   4. Plugin log isolation
 *   5. Event emission for DevTools
 *
 * These wrappers ensure that no plugin handler can crash the host.
 */

import { usePluginStabilityStore, SLOW_HANDLER_THRESHOLD_MS } from './plugin-stability-store';
import { usePluginLogStore } from './plugin-log-store';
import { usePluginEventBusStore } from '../plugin-event-bus';
import type { StabilityEventKind } from './plugin-stability-store';

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

function recordSlowHandler(
  pluginId: string,
  handlerType: string,
  handlerName: string,
  durationMs: number,
): void {
  const stabilityStore = usePluginStabilityStore.getState();
  stabilityStore.recordEvent(pluginId, 'slow-handler', `Slow ${handlerType}: ${handlerName} (${durationMs.toFixed(1)}ms)`, {
    handlerType,
    handlerName,
    durationMs,
    threshold: SLOW_HANDLER_THRESHOLD_MS,
  });

  const logStore = usePluginLogStore.getState();
  logStore.recordPerfMetric(pluginId, `slow-${handlerType}`, durationMs, { handlerName });
}

function recordError(
  pluginId: string,
  kind: StabilityEventKind,
  handlerType: string,
  handlerName: string,
  error: unknown,
): void {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  const stabilityStore = usePluginStabilityStore.getState();
  stabilityStore.recordEvent(pluginId, kind, `${handlerType} error: ${handlerName} — ${message}`, {
    handlerType,
    handlerName,
    stack,
  });

  const logStore = usePluginLogStore.getState();
  logStore.appendLog(pluginId, 'error', `${handlerType} error in ${handlerName}: ${message}`, handlerType, {
    stack,
  });
}

function emitErrorEvent(
  eventName: string,
  pluginId: string,
  handlerName: string,
  error: unknown,
): void {
  try {
    usePluginEventBusStore.getState().emit('system', eventName, {
      pluginId,
      handlerName,
      error: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString(),
    });
  } catch {
    /* best-effort */
  }
}

// ────────────────────────────────────────────────────────
// Action Handler Wrapper
// ────────────────────────────────────────────────────────

/**
 * Wrap an action handler to catch sync/async errors, detect slow execution,
 * record in stability store, and emit plugin.action.error on failure.
 */
export function wrapActionHandler<TArgs extends unknown[], TResult>(
  pluginId: string,
  actionName: string,
  handler: (...args: TArgs) => TResult | Promise<TResult>,
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs): Promise<TResult> => {
    const start = performance.now();
    try {
      const result = await handler(...args);
      const duration = performance.now() - start;
      if (duration > SLOW_HANDLER_THRESHOLD_MS) {
        recordSlowHandler(pluginId, 'action', actionName, duration);
      }
      return result;
    } catch (error) {
      recordError(pluginId, 'action-error', 'action', actionName, error);
      emitErrorEvent('plugin.action.error', pluginId, actionName, error);
      console.error(`[PluginSandbox] Action handler error in "${pluginId}:${actionName}":`, error);
      throw error; // Re-throw so the caller (deferred action) can reject cleanly
    }
  };
}

// ────────────────────────────────────────────────────────
// Event Handler Wrapper
// ────────────────────────────────────────────────────────

/**
 * Wrap an event handler to catch errors, prevent event bus breakage,
 * record in stability store, and emit plugin.event.error on failure.
 */
export function wrapEventHandler<T>(
  pluginId: string,
  eventName: string,
  handler: (event: T) => void,
): (event: T) => void {
  return (event: T): void => {
    const start = performance.now();
    try {
      handler(event);
      const duration = performance.now() - start;
      if (duration > SLOW_HANDLER_THRESHOLD_MS) {
        recordSlowHandler(pluginId, 'event', eventName, duration);
      }
    } catch (error) {
      recordError(pluginId, 'event-error', 'event', eventName, error);
      emitErrorEvent('plugin.event.error', pluginId, eventName, error);
      console.error(`[PluginSandbox] Event handler error in "${pluginId}" for "${eventName}":`, error);
      // Do NOT re-throw — prevent event bus from breaking
    }
  };
}

// ────────────────────────────────────────────────────────
// ContentSync Subscriber Wrapper
// ────────────────────────────────────────────────────────

/**
 * Wrap a contentSync subscriber to catch errors, prevent contentSync breakage,
 * record in stability store, and emit plugin.sync.error on failure.
 */
export function wrapSyncSubscriber<T>(
  pluginId: string,
  entityId: string,
  handler: (update: T) => void,
): (update: T) => void {
  return (update: T): void => {
    const start = performance.now();
    try {
      handler(update);
      const duration = performance.now() - start;
      if (duration > SLOW_HANDLER_THRESHOLD_MS) {
        recordSlowHandler(pluginId, 'sync', entityId, duration);
      }
    } catch (error) {
      recordError(pluginId, 'sync-error', 'contentSync', entityId, error);
      emitErrorEvent('plugin.sync.error', pluginId, entityId, error);
      console.error(`[PluginSandbox] ContentSync subscriber error in "${pluginId}" for "${entityId}":`, error);
      // Do NOT re-throw — prevent contentSync from breaking
    }
  };
}

// ────────────────────────────────────────────────────────
// Modal Callback Wrapper
// ────────────────────────────────────────────────────────

/**
 * Wrap a modal callback to catch errors, detect slow execution,
 * and record in stability store.
 */
export function wrapModalCallback<TArgs extends unknown[], TResult>(
  pluginId: string,
  callbackName: string,
  handler: (...args: TArgs) => TResult | Promise<TResult>,
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs): Promise<TResult> => {
    const start = performance.now();
    try {
      const result = await handler(...args);
      const duration = performance.now() - start;
      if (duration > SLOW_HANDLER_THRESHOLD_MS) {
        recordSlowHandler(pluginId, 'modal', callbackName, duration);
      }
      return result;
    } catch (error) {
      recordError(pluginId, 'action-error', 'modal', callbackName, error);
      console.error(`[PluginSandbox] Modal callback error in "${pluginId}:${callbackName}":`, error);
      throw error;
    }
  };
}
